import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

interface ImageWithPlaceholderProps {
    src: string;
    alt?: string;
    className?: string;
    placeholderClassName?: string;
    fallbackSrc?: string;
}

const ImageWithPlaceholder: React.FC<ImageWithPlaceholderProps> = ({
    src,
    alt = "",
    className,
    placeholderClassName,
    fallbackSrc,
}) => {
    const [currentSrc, setCurrentSrc] = useState(src || fallbackSrc || "");
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(!src && !fallbackSrc);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const prevSrcRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const next = src || fallbackSrc || "";
        const prev = prevSrcRef.current;
        prevSrcRef.current = next;

        // Skip the mount pass. Cached/small logos can fire onLoad before this
        // effect; resetting loaded here would hide them forever.
        if (prev === undefined || prev === next) return;

        setCurrentSrc(next);
        setLoaded(false);
        setError(!src && !fallbackSrc);
    }, [src, fallbackSrc]);

    useLayoutEffect(() => {
        const img = imgRef.current;
        if (img?.complete && img.naturalWidth > 0) {
            setLoaded(true);
        }
    }, [currentSrc]);

    if (!currentSrc) {
        return <div className={placeholderClassName || "image-placeholder"}></div>;
    }

    return (
        <>
            {(!loaded || error) && !fallbackSrc && (
                <div className={placeholderClassName || "image-placeholder"}></div>
            )}

            {!error && (
                <img
                    ref={imgRef}
                    src={currentSrc}
                    alt={alt}
                    className={className}
                    style={!loaded && !fallbackSrc ? { display: "none" } : undefined}
                    onLoad={() => setLoaded(true)}
                    onError={() => {
                        if (fallbackSrc && currentSrc !== fallbackSrc) {
                            setCurrentSrc(fallbackSrc);
                            setError(false);
                            setLoaded(false);
                            return;
                        }
                        setError(true);
                    }}
                />
            )}
        </>
    );
};

export default ImageWithPlaceholder;
